'use client';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useGoogleExportZoc } from '../_hooks/fetchers/zoc';
import { useUserProfile } from './userProfileContext';
import { useNotificationContext } from './globalNotificationContext';
import InfoDialog from '../(zoc)/zoc/components/infoDialog';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { TrackerClient } from '../_components/monitoring/tracking';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import moment from 'moment';

export type BackgroundOperationsContextType = {
  exportNoteToGoogle: (
    zocId: string,
    title: string,
    course: string,
    date: string
  ) => void;
  isMutating: boolean;
};

export const BackgroundOperationsContext =
  createContext<BackgroundOperationsContextType | null>(null);

const BackgroundOperationsProvider: React.FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  const { setToast, clearToast } = useNotificationContext();
  const { selectedLanguage, googleToken, setGoogleToken } = useUserProfile();
  const [open, setOpen] = useState<boolean>(false);
  const [zocId, setZocId] = useState<string>('');
  const [zocTitle, setZocTitle] = useState<string>('');
  const [zocCourse, setZocCourse] = useState<string>('');
  const [zocDate, setZocDate] = useState<string>('');

  const {
    trigger: triggerExport,
    isMutating,
    data,
    error,
  } = useGoogleExportZoc(selectedLanguage);

  const showErrorToast = () => {
    setToast({
      message: 'Sorry, an error occurred during the export process.',
      kind: 'error',
      key: `export-g-err-${zocId}`,
    });
  };
  const handleLoginSuccess = (tokenResponse: TokenResponse) => {
    clearToast('export-g-err');
    setGoogleToken(tokenResponse.access_token);
    showInfoToast(zocId);
    triggerExport({ zocId, googleToken: tokenResponse.access_token });
  };

  const showError = (msg?: string) => {
    datadogLogs.logger.error('Export to Google - signin failed');
    setToast({
      message:
        msg ||
        'Oops! Google sign-in failed. Refresh, check your connection, and allow pop-ups. Try a different browser or clear your cache if needed.',
      kind: 'error',
      key: 'export-g-err',
    });
  };

  const login = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: (error) => showError(error.error_description),
    onNonOAuthError: (error) => {
      if (error.type === 'popup_failed_to_open' || error.type === 'unknown') {
        showError();
      }
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  useEffect(() => {
    if (!!error) {
      clearToast(`export-g-${zocId}`);
      if (error.response?.status === 498) {
        setGoogleToken(undefined);
        login();
      } else {
        if (error.response?.status === 500) {
          setGoogleToken(undefined);
        }
        datadogLogs.logger.error(`Export to Google failed: ${zocId}`);
        setOpen(false);
        showErrorToast();
      }
    }
  }, [error]);

  useEffect(() => {
    if (!!data) {
      clearToast(`export-g-${zocId}`);
      setOpen(true);
      datadogLogs.logger.info(`Note was exported to google drive: ${zocId}`);
      datadogRum.addAction('Note was exported to google drive');
      TrackerClient.exportedToGoogle(zocId);
    }
  }, [data]);

  const showInfoToast = (zocId: String) => {
    setToast({
      message: 'Your note is being exported. It might take a few moments.',
      kind: 'info',
      key: `export-g-${zocId}`,
    });
  };

  const exportNoteToGoogle = (
    zocId: string,
    title: string,
    course: string,
    date: string
  ) => {
    setZocId(zocId);
    setZocTitle(title);
    setZocCourse(course);
    setZocDate(date);
    clearToast(`export-g-${zocId}`);
    clearToast('export-g-err');
    if (!!googleToken) {
      triggerExport({ zocId, googleToken });
      showInfoToast(zocId);
    } else {
      login();
    }
  };

  return (
    <BackgroundOperationsContext.Provider
      value={{ exportNoteToGoogle, isMutating }}
    >
      <InfoDialog
        tertiaryButtonLabel="Close"
        tertiaryButtonAction={() => setOpen(false)}
        isOpen={open}
        handleClose={() => setOpen(false)}
        title="Export to Google Drive Complete"
      >
        <p>We successfully exported these documents for you:</p>
        {data?.map((document: any, i: number) => (
          <a
            className="text-purple9 underline hover:text-purple5"
            key={i}
            href={document.link}
            target="_blank"
          >
            <p>
              Zoc - {zocTitle} - {zocCourse} -
              {moment(new Date(zocDate)).format('YYYY-MM-DD')} -
              <span className="uppercase">{document.language}</span>
            </p>
          </a>
        ))}
      </InfoDialog>
      {children}
    </BackgroundOperationsContext.Provider>
  );
};

const useBackgroundOperations = () => {
  const context = useContext(BackgroundOperationsContext);
  if (!context) {
    throw new Error(
      `useBackgroundOperations must be used within a BackgroundOperationsProvider`
    );
  }
  return context;
};

export default BackgroundOperationsProvider;
export { useBackgroundOperations };
